import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Video, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { resolveAvatarUrl } from '../lib/utils';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CallsPage = () => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCalls = async () => {
      try {
        const res = await axios.get(`${API}/calls/history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCalls(res.data || []);
      } catch {
        setCalls([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCalls();
  }, [token]);

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000 && d.getDate() === now.getDate()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diff < 172800000) return 'Yesterday';
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const formatDuration = (start, end) => {
    if (!start || !end) return '';
    const sec = Math.round((new Date(end) - new Date(start)) / 1000);
    if (sec < 60) return `${sec}s`;
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}m ${s}s`;
  };

  const getCallInfo = (call) => {
    const isOutgoing = call.from_user_id === user?.id;
    const otherUser = isOutgoing ? call.to_user : call.from_user;
    const isMissed = call.status === 'rejected' || (call.status === 'ended' && !call.answered_at);
    const isVideo = call.call_type === 'video';

    let icon, color, label;
    if (isMissed && !isOutgoing) {
      icon = PhoneMissed;
      color = 'text-red-500';
      label = 'Missed';
    } else if (isMissed && isOutgoing) {
      icon = PhoneOutgoing;
      color = 'text-red-400';
      label = 'No answer';
    } else if (isOutgoing) {
      icon = PhoneOutgoing;
      color = 'text-green-500';
      label = 'Outgoing';
    } else {
      icon = PhoneIncoming;
      color = 'text-green-500';
      label = 'Incoming';
    }

    const duration = call.answered_at ? formatDuration(call.answered_at, call.ended_at) : '';

    return { isOutgoing, otherUser, isMissed, isVideo, icon, color, label, duration };
  };

  return (
    <div className={`h-screen flex flex-col pb-14 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <div className={`px-4 pt-12 pb-3 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`} data-testid="calls-page-title">
          Calls
        </h1>
      </div>

      {/* Call List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-[#D4AF37]" />
          </div>
        ) : calls.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <Phone size={32} className={isDarkMode ? 'text-gray-600' : 'text-gray-300'} />
            </div>
            <p className={`text-base font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>No calls yet</p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Start a call from any chat</p>
          </div>
        ) : (
          calls.map((call) => {
            const info = getCallInfo(call);
            const CallIcon = info.icon;
            return (
              <div
                key={call.id}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  isDarkMode ? 'hover:bg-gray-800 active:bg-gray-800' : 'hover:bg-gray-50 active:bg-gray-50'
                }`}
                data-testid={`call-item-${call.id}`}
              >
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {info.otherUser?.avatar_url ? (
                    <img
                      src={resolveAvatarUrl(info.otherUser.avatar_url)}
                      alt={info.otherUser.display_name}
                      className="w-12 h-12 rounded-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium text-lg"
                    style={info.otherUser?.avatar_url ? { display: 'none' } : {}}>
                    {(info.otherUser?.display_name || '?').charAt(0).toUpperCase()}
                  </div>
                </div>

                {/* Info — click name to go to chat */}
                <button className="flex-1 min-w-0 text-left" onClick={() => {
                  const otherUserId = info.otherUser?.id;
                  if (otherUserId) navigate(`/chat/${call.chat_id || ''}`);
                }} data-testid={`call-chat-${call.id}`}>
                  <p className={`font-medium truncate ${
                    info.isMissed && !info.isOutgoing
                      ? 'text-red-500'
                      : isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {info.otherUser?.display_name || 'Unknown'}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <CallIcon size={14} className={info.color} />
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {info.label}{info.duration ? ` · ${info.duration}` : ''}
                    </span>
                  </div>
                </button>

                {/* Time + Call button */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {formatTime(call.started_at)}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const otherUserId = info.otherUser?.id;
                      if (!otherUserId) {
                        toast.error('User info not available');
                        return;
                      }
                      (async () => {
                        try {
                          const res = await axios.post(`${API}/calls/initiate`, {
                            to_user_id: otherUserId,
                            call_type: call.call_type || 'audio'
                          }, { headers: { Authorization: `Bearer ${token}` } });
                          navigate(`/call/${res.data.call_id}?type=${call.call_type || 'audio'}&userId=${otherUserId}`);
                        } catch (e) {
                          toast.error('Failed to initiate call');
                        }
                      })();
                    }}
                    className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    data-testid={`call-back-${call.id}`}
                  >
                    {info.isVideo ? (
                      <Video size={20} className="text-[#D4AF37]" />
                    ) : (
                      <Phone size={20} className="text-[#D4AF37]" />
                    )}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default CallsPage;
